import React from 'react';
import { TideCycleType } from '../../models'; // Assuming TideCycleType is exported from models
import { Box, Paper, Grid, Typography, Tooltip, IconButton, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WbSunnyIcon from '@mui/icons-material/WbSunny'; // For spring tide (full/new moon)
import NightlightIcon from '@mui/icons-material/Nightlight'; // For neap tide (quarter moon)
import { TideHeightPrediction, ServiceOpportunity, ServiceDefinition, DailySchedule } from '../../models'; // Import DailySchedule

interface MonthViewProps {
  dates: string[];
  selectedDate: string;
  tideDataByDate: Record<string, TideHeightPrediction[]>;
  opportunitiesByDate: Record<string, ServiceOpportunity[]>;
  optimizedOpportunitiesByDate?: Record<string, number[]>; // Indexes of optimized opportunities
  onDateSelect: (date: string) => void;
  onMonthChange?: (direction: 'prev' | 'next') => void; // Added prop for month navigation
  showDetailed?: boolean; // Whether to show detailed view with service-specific badges
  isLoading?: boolean; // Whether the data is currently loading
  serviceDefinitions?: ServiceDefinition[]; // Added service definitions prop
  dailyTideDataByDate?: Record<string, DailySchedule['tideData'] | undefined>; // New prop for tide data
}

// Component to render detailed opportunity tooltip content for a specific service
const ServiceOpportunityTooltip = ({
  opportunities,
  serviceId,
  serviceDefinitions // Pass service definitions down
}: {
  opportunities: ServiceOpportunity[],
  serviceId: string,
  serviceDefinitions: ServiceDefinition[] // Add prop
}) => {
  // Filter opportunities for just this service
  const serviceOpportunities = opportunities.filter(opp => opp.serviceId === serviceId);
  // Look up service name from definitions
  const serviceDef = serviceDefinitions.find(def => def.id === serviceId);
  const serviceName = serviceDef ? serviceDef.name : 'Unknown Service'; // Fallback name

  return (
    <Box sx={{ p: 1, width: '100%', minWidth: 320 }}>
      {/* Single header with service name */}
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
        {serviceName}
      </Typography>
      
      {/* Table view */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: '80px 80px 60px 60px',
        columnGap: 1,
        borderBottom: '1px solid rgba(255,255,255,0.3)',
        pb: 0.5,
        mb: 0.5,
        fontWeight: 'bold',
        fontSize: '0.75rem'
      }}>
        <Box>Start</Box>
        <Box>End</Box>
        <Box>Launch</Box>
        <Box>Retrieval</Box>
      </Box>
      
      {serviceOpportunities.map((opp, index) => (
        <Box key={index} sx={{ 
          display: 'grid',
          gridTemplateColumns: '80px 80px 60px 60px',
          columnGap: 1,
          py: 0.5,
          borderBottom: index < serviceOpportunities.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
          fontSize: '0.75rem'
        }}>
          <Box>{new Date(opp.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Box>
          <Box>{new Date(opp.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Box>
          <Box>{opp.tideHeightAtLaunch.toFixed(1)}m {opp.launchViable ? '✅' : '❌'}</Box>
          <Box>{opp.tideHeightAtRetrieval.toFixed(1)}m {opp.retrievalViable ? '✅' : '❌'}</Box>
        </Box>
      ))}
    </Box>
  );
};

// The MonthView component has no knowledge of how data is fetched or cached
// It only knows how to render the data it receives through props
// Helper function to convert TideCycleType values to string representation
// Handles both numeric values (from backend JSON) and string values (from TypeScript type)
const getTideCycleTypeString = (type: any): string => {
  if (type === undefined || type === null) return 'N/A';
  
  // If it's already a string, just return it
  if (typeof type === 'string') return type;
  
  // Handle numeric values from backend
  switch (Number(type)) {
    case 0: return 'Spring';   // TideCycleType.Spring
    case 1: return 'Neap';     // TideCycleType.Neap
    case 2: return 'Intermediate'; // TideCycleType.Intermediate
    default: return 'N/A';
  }
};

const MonthView: React.FC<MonthViewProps> = ({
  dates,
  selectedDate,
  tideDataByDate,
  opportunitiesByDate,
  onDateSelect,
  onMonthChange,
  showDetailed = false, // Default to simple view
  isLoading = false,
  serviceDefinitions = [], // Default to empty array
  dailyTideDataByDate = {} // Default for the new prop
}) => {
  // Get month and year from selected date
  const currentDate = new Date(selectedDate);
  const month = currentDate.getMonth();
  const year = currentDate.getFullYear();
  
  // Calculate first day of month
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  
  // Calculate starting day (for blank spaces) - Sunday is 0
  const startingDayOfWeek = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();
  
  // Create array of week rows
  const daysByWeek: Array<Array<number | null>> = [];
  let currentWeek: Array<number | null> = [];
  
  // Add blank days for first week
  for (let i = 0; i < startingDayOfWeek; i++) {
    currentWeek.push(null);
  }
  
  // Add days to weeks
  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day);
    
    if (currentWeek.length === 7) {
      daysByWeek.push(currentWeek);
      currentWeek = [];
    }
  }
  
  // Add any remaining days to the last week
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) {
      currentWeek.push(null);
    }
    daysByWeek.push(currentWeek);
  }
  
  // Format a day for use as a date string
  const formatDateString = (day: number): string => {
    const formattedDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return formattedDate;
  };
  
  // Check if date has opportunities
  const hasOpportunities = (dateString: string): boolean => {
    const opportunities = opportunitiesByDate[dateString];
    return opportunities && opportunities.length > 0;
  };
  
  // Group opportunities by service type
  const getOpportunitiesByService = (dateString: string) => {
    const opportunities = opportunitiesByDate[dateString] || [];
    const result: Record<string, ServiceOpportunity[]> = {};
    
    opportunities.forEach(opp => {
      if (!result[opp.serviceId]) {
        result[opp.serviceId] = [];
      }
      result[opp.serviceId].push(opp);
    });
    
    return result;
  };
  
  // Get the highest tide for a date
  const getHighTide = (dateString: string): number | null => {
    const tideData = tideDataByDate[dateString];
    
    if (!tideData || tideData.length === 0) return null;
    
    let maxHeight = tideData[0].height;
    tideData.forEach(tide => {
      if (tide.height > maxHeight) {
        maxHeight = tide.height;
      }
    });
    
    return maxHeight;
  };

  // Get service color with strict error handling
  const getServiceColor = (serviceId: string): string => {
    const serviceDef = serviceDefinitions.find(def => def.id === serviceId);
    if (!serviceDef) {
      console.error(`Service definition not found for ID: ${serviceId}`);
      throw new Error(`Data integrity error: Missing service definition for ID: ${serviceId}`);
    }
    return serviceDef.color;
  };
  
 
  // Handle month navigation - simply notify parent component
  // The UI has no knowledge of caching or data fetching
  const handleMonthNavigate = (direction: 'prev' | 'next') => {
    if (onMonthChange) {
      onMonthChange(direction);
    }
  };

  return (
    <Box sx={{ width: '100%', mb: 4, position: 'relative' }}>
      {/* Loading overlay */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10,
            borderRadius: 1
          }}
        >
          <CircularProgress size={40} />
        </Box>
      )}
      {/* Month header with navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
        <IconButton 
          onClick={() => handleMonthNavigate('prev')}
          size="small"
          aria-label="Previous month"
          disabled={isLoading} // Disable during loading
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" align="center" sx={{ mx: 2 }}>
          {new Date(year, month, 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
        </Typography>
        <IconButton 
          onClick={() => handleMonthNavigate('next')}
          size="small"
          aria-label="Next month"
          disabled={isLoading} // Disable during loading
        >
          <ArrowForwardIcon />
        </IconButton>
      </Box>
      
      {/* Service Legend moved to SearchContextBar */}
      
      {/* Day of week headers */}
      <Grid container spacing={1} sx={{ mb: 1 }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <Grid key={day} item xs={12/7}>
            <Typography align="center" variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {day}
            </Typography>
          </Grid>
        ))}
      </Grid>
      
      {/* Calendar grid */}
      {daysByWeek.map((week, weekIndex) => (
        <Grid container spacing={1} key={`week-${weekIndex}`} sx={{ mb: 1 }}>
          {week.map((day, dayIndex) => {
            // For empty slots
            if (day === null) {
              return (
                <Grid key={`empty-${weekIndex}-${dayIndex}`} item xs={12/7}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      height: '80px', 
                      backgroundColor: 'transparent',
                      p: 1 
                    }}
                  />
                </Grid>
              );
            }
            
            const dateString = formatDateString(day);
            const isAvailable = dates.includes(dateString);
            const isSelected = dateString === selectedDate;
            const hasServiceOpportunities = hasOpportunities(dateString);
            const highTide = getHighTide(dateString);
            
            return (
              <Grid key={`day-${dateString}`} item xs={12/7}>
                <Paper 
                  elevation={isSelected ? 6 : 1}
                  sx={{ 
                    height: '80px',
                    p: 1,
                    cursor: isAvailable ? 'pointer' : 'default',
                    border: isSelected ? '2px solid #1976d2' : 'none',
                    backgroundColor: hasServiceOpportunities ? 'rgba(76, 175, 80, 0.1)' : undefined,
                    opacity: isAvailable ? 1 : 0.7,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      elevation: isAvailable ? 3 : 1,
                      backgroundColor: isAvailable 
                        ? (hasServiceOpportunities ? 'rgba(76, 175, 80, 0.2)' : 'rgba(25, 118, 210, 0.08)')
                        : undefined
                    },
                    position: 'relative' // For the tide indicator
                  }}
                  onClick={() => isAvailable && onDateSelect(dateString)}
                >
                  <Typography 
                    variant="body1" 
                    align="center"
                    color={isSelected ? 'primary' : 'textPrimary'}
                    sx={{ fontWeight: isSelected ? 'bold' : 'normal' }}
                  >
                    {day}
                  </Typography>

                  {/* Moon phase angle indicator */}
                  {dailyTideDataByDate?.[dateString]?.astronomicalData?.moonPhaseAngle !== undefined && (
                    <Tooltip title={`Moon phase angle: ${dailyTideDataByDate[dateString]?.astronomicalData?.moonPhaseAngle.toFixed(2)} degrees`}>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '2px',
                          left: '2px',
                          fontSize: '0.75rem',
                        }}
                      >
                        {dailyTideDataByDate[dateString]?.astronomicalData?.moonPhaseAngle.toFixed(0)}°
                      </Box>
                    </Tooltip>
                  )}
                  
                  {/* Indicators for high tide */}
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      backgroundColor: highTide ? '#bbdefb' : 'transparent',
                      opacity: 0.8
                    }}
                  />
                  
                  {/* Tide cycle indicator with icon */}
                  {dailyTideDataByDate?.[dateString]?.tideCycleType !== undefined && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                      }}
                    >
                      {Number(dailyTideDataByDate[dateString]?.tideCycleType) === 0 ? ( // Spring
                        <Tooltip title="Spring tide">
                          <WbSunnyIcon fontSize="small" color="warning" />
                        </Tooltip>
                      ) : Number(dailyTideDataByDate[dateString]?.tideCycleType) === 1 ? ( // Neap
                        <Tooltip title="Neap tide">
                          <NightlightIcon fontSize="small" color="primary" />
                        </Tooltip>
                      ) : null}
                    </Box>
                  )}
                  
                  {/* Service opportunity badges - centered below day number */}
                  {hasServiceOpportunities && (
                    <Box sx={{ 
                      position: 'absolute', 
                      top: '60%', 
                      left: '50%', 
                      transform: 'translateX(-50%)',
                      display: 'flex', 
                      justifyContent: 'center',
                      flexWrap: 'wrap',
                      gap: '4px',
                      width: '80%'
                    }}>
                      {Object.entries(getOpportunitiesByService(dateString)).map(([serviceId, opportunities]) => (
                        <Tooltip
                          key={serviceId}
                          title={<ServiceOpportunityTooltip
                            opportunities={opportunitiesByDate[dateString]}
                            serviceId={serviceId}
                            serviceDefinitions={serviceDefinitions} // Pass definitions to tooltip
                          />}
                          arrow
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: 'rgba(0,0,0,0.85)',
                                maxWidth: 'none !important',
                                '& .MuiTooltip-arrow': {
                                  color: 'rgba(0,0,0,0.85)',
                                },
                              }
                            }
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor: getServiceColor(serviceId),
                              color: 'white',
                              borderRadius: '12px',
                              padding: '0 4px',
                              fontSize: '0.7rem',
                              minWidth: '16px',
                              textAlign: 'center',
                              fontWeight: 'bold'
                            }}
                          >
                            {opportunities.length}
                          </Box>
                        </Tooltip>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      ))}
    </Box>
  );
};

export default MonthView;
